import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Globe, School, X, Mic } from 'lucide-react';
import { logEvent } from 'firebase/analytics';
import { 
  collection, query, where, onSnapshot, addDoc, deleteDoc, getDocs,
  serverTimestamp, doc, getDoc, updateDoc, arrayUnion
} from 'firebase/firestore';
import { db, auth, analytics } from '../firebase';
import sparkLogo from '../assets/spark-logo.png';
import { useIdentity } from '../context/IdentityContext';

const statusMessages = [
  'Scanning group frequencies...',
  'Finding a voice room...',
  'Gathering participants...',
  'Almost there...',
];

const randomNames = [
  'Mystic', 'Shadow', 'Echo', 'Blaze', 'Luna', 'Nova', 'Sky', 'Zephyr', 'Raven',
  'Vortex', 'Nebula', 'Drift', 'Vibe', 'Frost', 'Phantom', 'Wraith', 'Dusk',
  'Haze', 'Storm', 'Rogue', 'Pulse', 'Ember', 'Flicker', 'Shade', 'Bolt', 'Glimmer',
  'Surge', 'Flare', 'Cosmo', 'Daze', 'Sable', 'Nyx', 'Rift', 'Glow', 'Specter',
  'Chill', 'Havoc', 'Mist', 'Stryke', 'Zest', 'Dawn', 'Volt', 'Crimson', 'Slate',
  'Kaze', 'Fury', 'Vapor', 'Glint', 'Rebel', 'Ash', 'Tide', 'Zenith', 'Dagger',
  'Star', 'Nerve', 'Blitz', 'Shadez', 'Flux', 'Neon', 'Chaos', 'Verve', 'Rush',
  'Grit', 'Faze', 'Drip', 'Hex', 'Jolt', 'Wisp', 'Scorch', 'Vex', 'Hype', 'Frosty',
  'Slash', 'Glaze', 'Sparkz', 'Eclipse', 'Raze', 'Sway', 'Phantomz', 'Buzz', 'Quartz',
  'Vivid', 'RUSH', 'Craze', 'Driftz', 'Ignite', 'Zest', 'Frenzy', 'Spectral', 'BlazeX',
  'Vaporz', 'Sting', 'Aurora', 'Kryo'
];

const getRandomAlias = () => randomNames[Math.floor(Math.random() * randomNames.length)];

export const GroupVoiceSetupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { identityMode } = useIdentity();
  const initialMode = location.state?.selectedMode || 'global';
  const userCollege = location.state?.userCollege || 'Unknown';
  const [currentMode, setCurrentMode] = useState(initialMode);

  const [statusIndex, setStatusIndex] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);
  const [micGranted, setMicGranted] = useState(false);
  const [avatarIndex, setAvatarIndex] = useState(0);
  const [canSearch, setCanSearch] = useState(false);
  
  const hasNavigatedRef = useRef(false);
  const queueDocIdRef = useRef(null);
  const minimumTimeRef = useRef(Date.now() + 2500);

  // Route Protection: Prevent direct URL access
  useEffect(() => {
    if (!location.state || !location.state.selectedMode) {
      navigate('/', { replace: true });
    }
  }, [location, navigate]);

  // Cycling Status Texts
  useEffect(() => {
    const statusInterval = setInterval(() => {
        setStatusIndex((prev) => (prev + 1) % statusMessages.length);
    }, 3000);
    return () => clearInterval(statusInterval);
  }, []);

  // Timer
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTimerSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timerInterval);
  }, []);

  // Cycling Avatars
  useEffect(() => {
    const avatarInterval = setInterval(() => {
        setAvatarIndex((prev) => (prev + 2) % randomNames.length);
    }, 1500);
    return () => clearInterval(avatarInterval);
  }, []);

  // Request Microphone permissions immediately
  useEffect(() => {
      const getPermissions = async () => {
          try {
              const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
              stream.getTracks().forEach(track => track.stop());
              setMicGranted(true);
              setIsInitializing(false);
          } catch (e) {
              toast.error("Microphone permission is required for Voice Calls.");
              navigate('/');
          }
      };
      getPermissions();
  }, [navigate]);

  // Search Delay Timer
  useEffect(() => {
      const delayTimer = setTimeout(() => {
          setCanSearch(true);
      }, 1500);
      return () => clearTimeout(delayTimer);
  }, []);

  // Queue Logic for Group Voice
  useEffect(() => {
    if (!micGranted || isInitializing || !canSearch) return;

    const user = auth.currentUser;
    if (!user) {
      navigate('/login');
      return;
    }

    let isMatched = false;

    // Listen for group room where we are a participant
    const roomQuery = query(
        collection(db, 'groupVoiceRooms'), 
        where('participantUids', 'array-contains', user.uid), 
        where('ended', '==', false)
    );

    const unsubRoom = onSnapshot(roomQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
            if (change.type === 'added' && !hasNavigatedRef.current) {
                hasNavigatedRef.current = true;
                if (queueDocIdRef.current) {
                    deleteDoc(doc(db, 'groupVoiceQueue', queueDocIdRef.current)).catch(() => {});
                }
                const rData = change.doc.data();
                
                const delay = Math.max(0, minimumTimeRef.current - Date.now());
                setTimeout(() => {
                    navigate(`/group-voice-room/${change.doc.id}`, { 
                        replace: true, 
                        state: { roomData: rData } 
                    });
                }, delay);
            }
        });
    });

    // Attempt to match or queue
    const attemptMatchOrQueue = async () => {
      if (queueDocIdRef.current || hasNavigatedRef.current || isMatched) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const uData = userDoc.data() || {};
        const myAlias = getRandomAlias();
        const myCollege = uData.college || 'Unknown';

        // Step 1: Check for existing open group rooms with space (< 4 participants)
        const openRoomsQuery = query(
            collection(db, 'groupVoiceRooms'),
            where('ended', '==', false),
            where('mode', '==', currentMode)
        );
        const openRoomsSnap = await getDocs(openRoomsQuery);
        
        let joinedExisting = false;
        for (const d of openRoomsSnap.docs) {
            const rData = d.data();
            const participants = rData.participantUids || [];
            
            // Don't join if already in it or if full
            if (participants.includes(user.uid) || participants.length >= 4) continue;
            
            // For campus mode, check college match
            if (currentMode === 'campus') {
                const roomCollege = rData.college;
                if (roomCollege && roomCollege !== myCollege) continue;
            }
            
            // Join this room!
            try {
                const roomDocRef = doc(db, 'groupVoiceRooms', d.id);
                const participants = rData.participantUids || [];
                
                // Only join if there's room
                if (participants.length >= 4) continue;

                await updateDoc(roomDocRef, {
                    participantUids: [...participants, user.uid],
                    [`participantInfo.${user.uid}`]: {
                        alias: myAlias,
                        college: myCollege,
                        voiceEffect: null,
                        muted: false,
                        joinedAt: Date.now(),
                        isActive: true
                    }
                });
                
                isMatched = true;
                hasNavigatedRef.current = true;
                
                const delay = Math.max(0, minimumTimeRef.current - Date.now());
                setTimeout(() => {
                    navigate(`/group-voice-room/${roomDocRef.id}`, { 
                        replace: true, 
                        state: { roomData: { ...rData, participantUids: [...participants, user.uid] } } 
                    });
                }, delay);
                joinedExisting = true;
                break;
            } catch (e) {
            }
        }
        
        if (joinedExisting) return;

        // Step 2: Check queue for another user to create a new room with
        const queueRef = collection(db, 'groupVoiceQueue');
        const qSearch = query(queueRef, where('mode', '==', currentMode));
        const snap = await getDocs(qSearch);
        
        let partner = null;
        for (const d of snap.docs) {
            const data = d.data();
            if (data.uid !== user.uid) {
                if (currentMode === 'global' || data.college === myCollege) {
                    partner = { docId: d.id, ...data };
                    break;
                }
            }
        }

        if (partner) {
            isMatched = true;
            hasNavigatedRef.current = true;
            
            const roomData = {
                participantUids: [partner.uid, user.uid],
                participantInfo: {
                    [partner.uid]: {
                        alias: partner.alias || getRandomAlias(),
                        college: partner.college || 'Unknown',
                        voiceEffect: null,
                        muted: false,
                        joinedAt: Date.now(),
                        isActive: true
                    },
                    [user.uid]: {
                        alias: myAlias,
                        college: myCollege,
                        voiceEffect: null,
                        muted: false,
                        joinedAt: Date.now(),
                        isActive: true
                    }
                },
                mode: currentMode,
                college: currentMode === 'campus' ? myCollege : null,
                createdAt: serverTimestamp(),
                ended: false
            };
            
            const roomRef = await addDoc(collection(db, 'groupVoiceRooms'), roomData);
            await deleteDoc(doc(db, 'groupVoiceQueue', partner.docId)).catch(() => {});
            
            const delay = Math.max(0, minimumTimeRef.current - Date.now());
            setTimeout(() => {
                navigate(`/group-voice-room/${roomRef.id}`, { 
                    replace: true, 
                    state: { roomData } 
                });
            }, delay);
            
        } else {
            // Clean up any old queue entries
            const existingQ = query(queueRef, where('uid', '==', user.uid));
            const existingSnap = await getDocs(existingQ);
            for (const d of existingSnap.docs) {
                await deleteDoc(d.ref).catch(() => {});
            }
            
            const queuePayload = {
                uid: user.uid,
                mode: currentMode,
                college: myCollege,
                alias: myAlias,
                timestamp: serverTimestamp()
            };
            
            try {
                const docRef = await addDoc(queueRef, queuePayload);
                queueDocIdRef.current = docRef.id;
                
                logEvent(analytics, 'join_queue', { 
                    type: '4-4_voice', 
                    initial_mode: currentMode 
                });
            } catch (qErr) {
            }
        }
      } catch (e) {
      }
    };

    attemptMatchOrQueue();

    // Cleanup on unmount or when mode changes
    return () => {
        unsubRoom();
        if (queueDocIdRef.current && !hasNavigatedRef.current) {
            deleteDoc(doc(db, 'groupVoiceQueue', queueDocIdRef.current)).catch(() => {});
        }
    };
  }, [micGranted, isInitializing, navigate, currentMode, canSearch]);

  const handleCancel = () => {
      if (queueDocIdRef.current) {
          hasNavigatedRef.current = true;
          deleteDoc(doc(db, 'groupVoiceQueue', queueDocIdRef.current)).catch(() => {});
      }
      navigate('/', { replace: true });
  };

  const handleSwitchToGlobal = async () => {
      if (queueDocIdRef.current) {
          await deleteDoc(doc(db, 'groupVoiceQueue', queueDocIdRef.current)).catch(() => {});
          queueDocIdRef.current = null;
      }
      
      logEvent(analytics, 'switch_to_global_clicked', { type: '4-4_voice' });
      setCurrentMode('global');
  };

  const formatTime = (seconds) => {
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  if (isInitializing) {
     return (
        <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-center">
            <Mic className="text-[#0BD9E5] w-12 h-12 mb-6 animate-pulse" />
            <h2 className="text-white text-xl font-bold mb-2">Microphone Access</h2>
            <p className="text-gray-400 text-sm">Please allow microphone access to join the group voice room.</p>
        </div>
     );
  }

  if (identityMode === 'real') {
     return (
       <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col items-center justify-center p-8 shrink-0 relative bg-[#0a0a0a]">
          <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 text-[#0BD9E5] flex items-center justify-center mb-6">
             <Users size={32} />
          </div>
          <h2 className="text-[31px] font-extrabold mb-2 leading-tight text-center text-white">4-4 Room</h2>
          <p className="text-gray-500 text-[15px] text-center">Coming soon for Real Identity mode.</p>
       </main>
     );
  }

  return (
    <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-black text-white font-poppins selection:bg-[#0BD9E5]/30">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <div className="absolute top-[20%] left-[20%] w-[40%] h-[40%] bg-[#0BD9E5]/10 blur-[100px] rounded-full mix-blend-screen animate-pulse" style={{ animationDuration: '4s' }} />
            <div className="absolute bottom-[20%] right-[20%] w-[40%] h-[40%] bg-purple-500/10 blur-[100px] rounded-full mix-blend-screen animate-pulse" style={{ animationDuration: '5s' }} />
        </div>

        <div className="relative z-10 flex flex-col h-full items-center justify-center p-6 w-full max-w-md mx-auto">
            <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-50">
                <button 
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors pointer-events-auto"
                >
                    <X size={20} className="text-white" />
                </button>
                <div className="w-10" />
            </div>

           {/* Avatar & Radar Centered Together */}
           <div className="relative w-72 h-72 flex items-center justify-center mb-8">
               {/* Radars */}
               <div className="absolute inset-0 border-2 rounded-full bg-black/40 flex items-center justify-center border-[#FF6600]">
                  {[0, 1, 2].map((i) => (
                     <motion.div 
                         key={i}
                         initial={{ scale: 0.5, opacity: 0.8 }}
                         animate={{ scale: 1.5, opacity: 0 }}
                         transition={{ duration: 2, repeat: Infinity, delay: i * 0.6 }}
                         className="absolute w-40 h-40 border rounded-full border-[#FF6600]"
                     />
                  ))}
               </div>

               {/* Avatar */}
               <motion.div 
                   animate={{ 
                      boxShadow: ['0 0 0px #FF6600', '0 0 40px #FF6600', '0 0 0px #FF6600']
                   }}
                   transition={{ duration: 2, repeat: Infinity }}
                   className="w-24 h-24 bg-[#111] border border-[#FF6600]/50 rounded-full flex items-center justify-center relative z-10 overflow-hidden"
               >
                    <AnimatePresence mode="wait">
                        <motion.img
                            key={avatarIndex}
                            src={`/avatars/${randomNames[avatarIndex]}.png`}
                            alt="avatar"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.3 }}
                            className="w-full h-full object-cover"
                        />
                    </AnimatePresence>
               </motion.div>
           </div>

           <AnimatePresence mode="wait">
               <motion.p 
                   key={statusIndex}
                   initial={{ opacity: 0, y: 10 }}
                   animate={{ opacity: 1, y: 0 }}
                   exit={{ opacity: 0, y: -10 }}
                   className="text-gray-300 text-lg font-medium text-center px-6 min-h-[28px] tracking-wide"
               >
                   {statusMessages[statusIndex]}
               </motion.p>
           </AnimatePresence>
           
           <p className="font-mono text-[#FF6600] font-bold text-lg mt-6 bg-[#FF6600]/10 px-4 py-1.5 rounded-full tracking-widest border border-[#FF6600]/20">
               {formatTime(timerSeconds)}
           </p>

           <div className="mt-8 flex items-center gap-2 text-gray-500 text-sm">
               <Users size={16} />
               <span>Up to 4 people per room</span>
           </div>

            <motion.div className="mt-8 flex flex-col items-center gap-4 w-full">
                {currentMode === 'global' ? (
                    <div className="px-5 py-2 rounded-full border border-[#BD00FF]/50 bg-[#BD00FF]/10 text-[#BD00FF] text-xs font-bold uppercase tracking-widest flex items-center gap-2 shadow-[0_0_15px_rgba(189,0,255,0.2)]">
                       <Globe size={14} /> Global Resonance
                    </div>
                ) : (
                    <>
                        <div className="px-5 py-2 rounded-full border border-[#0BD9E5]/50 bg-[#0BD9E5]/10 text-[#0BD9E5] text-xs font-bold uppercase tracking-widest flex items-center gap-2 shadow-[0_0_15px_rgba(11,217,229,0.2)]">
                           <School size={14} /> Campus Specific
                        </div>
                        <button
                            onClick={handleSwitchToGlobal}
                            className="px-5 py-2 rounded-full border border-[#BD00FF]/30 bg-[#BD00FF]/5 text-[#BD00FF] text-xs font-medium uppercase tracking-widest flex items-center gap-2 hover:bg-[#BD00FF]/15 transition-colors cursor-pointer"
                        >
                            <Globe size={14} /> Switch to Global
                        </button>
                    </>
                )}
            </motion.div>
        </div>
    </main>
  );
};

export default GroupVoiceSetupPage;
